import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart
} from 'recharts';
import {
  TrendingUp,
  Info
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';

/**
 * AverageResolutionTimeChart component for displaying average days to close issues by month
 * @returns {JSX.Element} AverageResolutionTimeChart component
 */
const AverageResolutionTimeChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch average resolution time data on component mount
  useEffect(() => {
    const fetchAverageResolutionTime = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to get average days to close by month
        const response = await authGet('/api/workitems/production-bugs/average-days-to-close-by-month');
        
        if (response.success) {
          setData(response.data || []);
          setError(null);
          console.log('Fetched average resolution time data:', response.data);
        } else {
          throw new Error(response.message || 'Failed to load average resolution time data');
        }
      } catch (err) {
        console.error('Error fetching average resolution time data:', err);
        setError('Failed to load average resolution time data. Please try again later.');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAverageResolutionTime();
  }, []);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ 
          bgcolor: 'background.paper', 
          border: '1px solid #ccc', 
          borderRadius: 1, 
          p: 1,
          boxShadow: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {`Month: ${label}`}
          </Typography>
          <Typography variant="body2" color="primary">
            {`Average Days to Close: ${payload[0].value} days`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`Total Issues: ${payload[0].payload.totalIssues || 0}`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Calculate trend
  const calculateTrend = () => {
    if (data.length < 2) return { trend: 'neutral', change: 0 };
    
    const recent = data.slice(-3); // Last 3 months
    const older = data.slice(-6, -3); // Previous 3 months
    
    if (recent.length === 0 || older.length === 0) return { trend: 'neutral', change: 0 };
    
    const recentAvg = recent.reduce((sum, item) => sum + item.averageDaysToClose, 0) / recent.length;
    const olderAvg = older.reduce((sum, item) => sum + item.averageDaysToClose, 0) / older.length;
    
    const change = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    return {
      trend: change > 5 ? 'up' : change < -5 ? 'down' : 'neutral',
      change: Math.abs(change)
    };
  };

  const trend = calculateTrend();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No data available for average resolution time by month.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <TrendingUp sx={{ mr: 1, color: '#1976d2' }} />
            <Typography variant="h6" component="h3">
              Average Days to Close Issues by Month
            </Typography>
            <MuiTooltip title="Shows the average number of days it took to close issues in each month">
              <IconButton size="small">
                <Info fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </Box>
        }
        subheader={
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Based on {data.reduce((sum, item) => sum + item.totalIssues, 0)} closed issues
            </Typography>
            {trend.change > 0 && (
              <Typography 
                variant="body2" 
                color={trend.trend === 'up' ? 'error.main' : trend.trend === 'down' ? 'success.main' : 'text.secondary'}
                sx={{ fontWeight: 'bold' }}
              >
                {trend.trend === 'up' ? '↗' : trend.trend === 'down' ? '↘' : '→'} 
                {trend.change.toFixed(1)}% vs previous period
              </Typography>
            )}
          </Box>
        }
      />
      <CardContent>
        <Box height={400}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="month" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                stroke="#666"
              />
              <YAxis 
                label={{ value: 'Days', angle: -90, position: 'insideLeft' }}
                fontSize={12}
                stroke="#666"
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Bar chart for average days to close */}
              <Bar 
                dataKey="averageDaysToClose" 
                fill="#1976d2" 
                name="Average Days to Close"
                radius={[4, 4, 0, 0]}
              />
              
              {/* Line chart for trend */}
              <Line 
                type="monotone" 
                dataKey="averageDaysToClose" 
                stroke="#ff9800" 
                strokeWidth={3}
                dot={{ fill: '#ff9800', strokeWidth: 2, r: 4 }}
                name="Trend Line"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Summary statistics */}
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Summary Statistics
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Average Resolution Time
              </Typography>
              <Typography variant="h6" color="primary">
                {Math.round((data.reduce((sum, item) => sum + item.averageDaysToClose, 0) / data.length) * 10) / 10} days
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Best Month
              </Typography>
              <Typography variant="h6" color="success.main">
                {data.reduce((min, item) => item.averageDaysToClose < min.averageDaysToClose ? item : min, data[0])?.month || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Issues Closed
              </Typography>
              <Typography variant="h6" color="text.primary">
                {data.reduce((sum, item) => sum + item.totalIssues, 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AverageResolutionTimeChart;
